import {
  Button,
  ButtonGroup,
  OutlineCircleQuestionIcon,
  Tooltip,
} from '@frontend/legacy-components';

import {
  ViewerPopoverButton,
  ViewerToolsBar,
} from 'modules/common/viewer/components';

import { isMobileBrowser } from 'utils/browserUtils';

import { useDxfViewerSession } from '../DxfViewerContext';
import { useDxfSelectionMode } from '../hooks/useDxfSelectionMode';
import { mappingValidationSaveBlock } from '../session/mappingValidation';
import { useReadableStore } from '../session/useSessionState';
import { DxfBrushToolGroup } from './DxfBrushToolGroup';
import { DxfSelectionModeGroup } from './DxfSelectionModeGroup';
import { DxfViewerControlsInfoRows } from './DxfViewerControlsInfoRows';

const NO_CHANGES_TO_SAVE = 'No changes to save';

export type DxfMappingToolbarProps = {
  /** Leaves mapping mode, confirming first when there are edits to discard. */
  onCancel: () => void;
  onSave: () => void;
  isSaving?: boolean;
};

/**
 * The second bar, shown in place of the viewing tools while mapping.
 *
 * Measure, axis dimensions and the bounding box are deliberately absent — the spec
 * scopes them to viewing mode, and `modeController` has already switched them off,
 * so there is nothing here to turn them back on with.
 */
export const DxfMappingToolbar = ({
  onCancel,
  onSave,
  isSaving = false,
}: DxfMappingToolbarProps) => {
  const session = useDxfViewerSession();
  const mapping = useReadableStore(session.mapping);
  const isDirty = useReadableStore(session.hasUnsavedMapping);
  const tool = useReadableStore(session.tools);
  const selectionMode = useDxfSelectionMode();
  const validation = useReadableStore(session.mappingValidation);
  const isMobile = isMobileBrowser();
  const isLocked = mapping.lockReason !== undefined;
  const validationBlock = mappingValidationSaveBlock(validation);

  const canSave =
    !isLocked && !validationBlock && (isDirty || mapping.saved === null);

  const saveDisabledReason = canSave
    ? undefined
    : mapping.lockReason ?? validationBlock ?? NO_CHANGES_TO_SAVE;
  const saveLabel =
    saveDisabledReason === undefined
      ? 'Save Mapping'
      : `Save Mapping. ${saveDisabledReason}`;

  return (
    <ViewerToolsBar px={0.75} data-testid="dxf-mapping-toolbar">
      <DxfSelectionModeGroup
        {...selectionMode}
        isDisabled={isLocked}
        disabledReason={mapping.lockReason}
      />

      <DxfBrushToolGroup
        activeTool={tool}
        onArmBrush={session.tools.armBrush}
        onChoose={() => session.tools.set('select')}
        isDisabled={isLocked}
        disabledReason={mapping.lockReason}
      />

      <ViewerPopoverButton
        title="Mapping controls"
        icon={<OutlineCircleQuestionIcon boxSize={3} />}
      >
        <DxfViewerControlsInfoRows isMobile={isMobile} isMapping />
      </ViewerPopoverButton>

      <ButtonGroup spacing={1}>
        <Button
          size="md"
          variant="secondary"
          onClick={onCancel}
          data-testid="dxf-mapping-cancel"
        >
          Cancel
        </Button>
        <Tooltip
          placement="bottom"
          label={saveDisabledReason}
          isDisabled={saveDisabledReason === undefined}
        >
          <Button
            size="md"
            onClick={onSave}
            isLoading={isSaving}
            isDisabled={!canSave}
            aria-label={saveLabel}
            data-testid="dxf-mapping-save"
          >
            Save Mapping
          </Button>
        </Tooltip>
      </ButtonGroup>
    </ViewerToolsBar>
  );
};
DxfMappingToolbar.displayName = 'DxfMappingToolbar';
